<template>
  <div class="layout-center-wrap">
    <!-- 상단 타이틀 -->
    <div class="page-tit__wrap">
      <h1 class="page-tit__sub" aria-label="현재 위치">개인정보 처리방침</h1>
      <p class="page-tit" aria-hidden="true">개인정보 처리방침</p>
    </div>
    <!-- 개인정보 처리방침 -->
    <section class="section-pt" aria-labelledby="intro-heading">
      <h2 id="intro-heading" class="section-title">개인정보 처리방침 안내</h2>
      <p class="desc-block">
        {{ parseOverview("title", items?.overview) }}
      </p>
      <div class="notice-box" role="note" aria-label="시행일 안내">
        <strong class="notice-box__tit">
          {{ parseOverview("content-date", items?.overview) }}</strong
        >
        <p class="notice-box__text">
          {{ parseOverview("content", items?.overview) }}
        </p>
      </div>
    </section>
    <!-- 주요 개인정보 처리 표시(라벨링) -->
    <section
      class="card-grid"
      aria-labelledby="labeling-heading"
      v-if="hisNum !== 1"
    >
      <h2 id="labeling-heading" class="section-title">
        주요 개인정보 처리 표시(라벨링)
      </h2>
      <ul class="card-grid__list" role="list">
        <li
          v-for="(block, idx) in items?.labelling"
          :key="idx"
          class="card-grid__item"
          :aria-labelledby="titleId(idx)"
        >
          <div class="card-grid__head">
            <div class="card-grid__icon" aria-hidden="true">
              <img
                v-if="getIconSrc(block.item).light"
                :src="getIconSrc(block.item).light"
                class="light-only"
              />
              <img
                v-if="getIconSrc(block.item).dark"
                :src="getIconSrc(block.item).dark"
                class="dark-only"
              />
            </div>

            <div class="card-grid__cont">
              <h3 :id="titleId(idx)" class="card-grid__title">
                {{ block.item }}
              </h3>
              <p class="card-grid__desc">{{ block.summary }}</p>
            </div>
          </div>

          <ul class="info-card__list" :aria-label="`${block.item} 항목`">
            <li
              v-for="detail in sortedDetails(block.detail)"
              :key="detail.order + '-' + detail.title"
              class="info-card__item"
            >
              <span class="info-card__label">{{ detail.title }}</span>
              <span
                class="info-card__value"
                v-dompurify-html="detail.content"
              ></span>
            </li>
          </ul>
        </li>
      </ul>
    </section>
    <!-- 목차 -->
    <section
      class="list-num-box"
      aria-labelledby="outline-heading"
      v-if="items?.toc?.length > 0 && hisNum !== 1"
    >
      <h2 id="outline-heading" class="section-title">목차</h2>
      <p class="desc-block">
        {{
          items?.toc[0]?.title ||
          "「개인정보 처리방침」은 다음과 같은 내용으로 구성되어 있습니다."
        }}
      </p>

      <div class="list-num-box__box" role="region" aria-label="목차 목록">
        <ol class="list-num-box__list">
          <li
            v-for="(item, idx) in items?.toc?.slice(1)"
            :key="item.order"
            class="list-num-box__item"
          >
            <p class="list-num-box__text">
              <span class="list-num-box__num" aria-hidden="true">{{
                idx + 1
              }}</span>
              {{ item.title }}
            </p>
          </li>
        </ol>
      </div>
    </section>
    <!-- 상세 내용 -->
    <section class="section-accordion__wrap">
      <h2 class="section-title">상세 내용</h2>
      <article
        v-for="(item, index) in accordionItems"
        :key="index"
        class="section-accordion__item"
      >
        <h3 class="section-accordion__header">
          <button
            type="button"
            class="section-accordion__btn"
            :aria-controls="`panel-${index + 1}`"
            :id="`accordion-trigger-${index + 1}`"
          >
            <span class="section-accordion__num" aria-hidden="true">{{
              index + 1
            }}</span>
            <span class="section-accordion__title" v-html="item.title"></span>
          </button>
        </h3>
        <div
          :id="`panel-${index + 1}`"
          class="section-accordion__panel"
          role="region"
          :aria-labelledby="`accordion-trigger-${index + 1}`"
        >
          <div class="section-accordion__content" @click="handleFileClick">
            <div v-dompurify-html="formatHtml(item.content)"></div>
          </div>
        </div>
      </article>
    </section>
    <!-- 이력 안내 -->
    <section class="section-pt" v-if="revisionHistoryList?.length > 0">
      <h2 class="section-title">이력 안내</h2>
      <div class="form-group">
        <select
          id="revisionHistory"
          v-model="selectedRevision"
          @change="handleChange"
        >
          <option
            v-for="(item, index) in revisionHistoryList"
            :key="index"
            :value="index"
          >
            {{ item }}
          </option>
        </select>
      </div>
    </section>
  </div>
</template>

<script>
import { inject, onMounted, ref } from "vue";
import { useStore } from "vuex";
import fu from "@/component/utils/FileUtils.js";

export default {
  setup() {
    const axios = inject("axios");
    const store = useStore();
    const showLoading = () => store.dispatch("loading/show");
    const hideLoading = () => store.dispatch("loading/hide");

    // data 정의
    const items = ref([]);
    const selectedOption = ref(0);
    const accordionItems = ref(null);
    const privacyPolicyData = ref(null);
    const revisionHistoryList = ref([]);
    const selectedRevision = ref("");
    const hisNum = ref(0);

    //methods 정의
    const getList = () => {
      showLoading();
      axios
        .post("/core/pu/view/privacy-policy/get", {})
        .then((res) => {
          if (res.data.resType === "RES_OK") {
            privacyPolicyData.value = res.data.resList;
            items.value = res.data.resList[0];
            parseMainContent();
            setRevisionHistoryList(res.data.resList);
          } else {
            items.value = [];
            alert(res.data.resMsg);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          hideLoading();
        });
    };
    const downloadFile = (dir, name) => {
      fu.downloadFile("/core/pu/view/download", dir, name);
    };
    const parseOverview = (type, data) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(data, "text/html");

      const message = doc.querySelector(type)?.textContent.trim() || "";
      return message;
    };

    const setRevisionHistoryList = (data) => {
      revisionHistoryList.value = data.map((item) => item.revisionHistory);
      if (revisionHistoryList.value.length > 0) {
        selectedRevision.value = 0;
      }
    };

    const handleChange = () => {
      showLoading();
      items.value = privacyPolicyData.value[selectedRevision.value];
      hisNum.value = items.value.hisNum;
      parseMainContent();

      setTimeout(() => {
        hideLoading();
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 500);
    };

    const parseMainContent = () => {
      accordionItems.value = items.value.mainContent
        .split(/<h4[^>]*>/)
        .filter((str) => str.trim() !== "")
        .map((str) => {
          const [titleMatch, ...rest] = str.split(/<\/h4>/);
          const title = titleMatch?.trim() || "";
          const content = rest.join("</h4>").trim();
          return { title, content };
        });
    };

    const formatHtml = (html) => {
      if (!html) return "";
      const parser = new DOMParser();

      const doc = parser.parseFromString(html, "text/html");

      // id가 txa로 시작하는 요소 선택
      doc.querySelectorAll('[id^="txa"]').forEach((el) => {
        // 기존 내용 가져오기
        const content = el.innerHTML.trim();

        // <p class="section-accordion__desc">로 치환
        el.innerHTML = `<p class="section-accordion__desc">${content}</p>`;
      });

      // 다시 HTML 문자열로 변환
      html = doc.body.innerHTML;

      // <table>은 div.wrap + table 클래스
      html = html.replace(
        /<table([^>]*)>/g,
        '<div class="data-table__wrap"><table class="data-table"$1>'
      );
      html = html.replace(/<\/table>/g, "</table></div>");

      html = replaceFileLinks(html);

      return html;
    };

    const replaceFileLinks = (html) => {
      return html.replace(/\[([^\]]+)\]\(file\)/g, (match, key) => {
        const file = items.value.file?.find((f) => f.key === key);
        if (!file) {
          return match;
        }

        return `
            <button
                type="button"
                class="file-link"
                data-path="${file.savePath}"
                data-name="${file.saveName}">
                ${file.saveName}
            </button>
            `;
      });
    };

    const handleFileClick = (e) => {
      const target = e.target.closest(".file-link");
      if (!target) {
        return;
      }

      const dir = "/" + target.dataset.path;
      const name = target.dataset.name;

      downloadFile(dir, name);
    };

    const titleId = (idx) => `card-title-${idx}`;

    const sortedDetails = (details = []) => {
      return [...details].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    };

    const iconMap = {
      "일반 개인정보 수집": {
        light: new URL("@/assets/img/icon_user-check.png", import.meta.url)
          .href,
        dark: new URL("@/assets/img/icon_user-check_dark.png", import.meta.url)
          .href,
      },
      "개인정보의 처리 목적": {
        light: new URL("@/assets/img/icon_file-goal.png", import.meta.url).href,
        dark: new URL("@/assets/img/icon_file-goal_dark.png", import.meta.url)
          .href,
      },
      "개인정보의 보유기간": {
        light: new URL("@/assets/img/icon_clock.png", import.meta.url).href,
        dark: new URL("@/assets/img/icon_clock_dark.png", import.meta.url).href,
      },
      "개인정보 제공": {
        light: new URL("@/assets/img/icon_handshake.png", import.meta.url).href,
        dark: new URL("@/assets/img/icon_handshake_dark.png", import.meta.url)
          .href,
      },
      "개인정보 처리위탁": {
        light: new URL("@/assets/img/icon_user-doc.png", import.meta.url).href,
        dark: new URL("@/assets/img/icon_user-doc_dark.png", import.meta.url)
          .href,
      },
      "개인정보 열람 청구": {
        light: new URL("@/assets/img/icon_user-doc.png", import.meta.url).href,
        dark: new URL("@/assets/img/icon_user-doc_dark.png", import.meta.url)
          .href,
      },
    };

    const getIconSrc = (item) => {
      return iconMap[item];
    };

    // life cycle 정의
    onMounted(() => {
      getList();
    });

    //html에서 사용될 데이터 정의
    return {
      selectedOption,
      items,
      parseOverview,
      getIconSrc,
      sortedDetails,
      titleId,
      accordionItems,
      formatHtml,
      revisionHistoryList,
      selectedRevision,
      handleChange,
      downloadFile,
      handleFileClick,
      hisNum,
    };
  },
};
</script>
<style scoped>
:deep(.file-link) {
  color: #0066cc;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}
:deep(a) {
  color: #0066cc;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}
</style>
